const DEFAULT_TIME_ZONE = 'America/Sao_Paulo';

const runtimeEnv = window.ENV ?? {};

const normalizeFixedOffset = (value: string): string => {
  const match = value.match(/^(?:GMT|UTC)?\s*([+-])(\d{1,2})(?::?00)?$/i);
  if (!match) return value;

  const hours = Number(match[2]);
  if (hours > 14) return value;

  // Os identificadores Etc/GMT usam o sinal invertido por definição do IANA.
  const ianaSign = match[1] === '-' ? '+' : '-';
  return hours === 0 ? 'Etc/GMT' : `Etc/GMT${ianaSign}${hours}`;
};

export const resolveTimeZone = (configuredTimeZone?: string): string => {
  const candidate = normalizeFixedOffset(configuredTimeZone?.trim() || DEFAULT_TIME_ZONE);

  try {
    new Intl.DateTimeFormat('pt-BR', { timeZone: candidate }).format();
    return candidate;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
};

export const APP_TIME_ZONE = resolveTimeZone(
  runtimeEnv.REACT_APP_TIME_ZONE || process.env.REACT_APP_TIME_ZONE
);

const ISO_DATE_TIME_WITHOUT_TIME_ZONE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export const parseApiDate = (value: string | number | Date): Date | null => {
  const normalizedValue =
    typeof value === 'string' && ISO_DATE_TIME_WITHOUT_TIME_ZONE.test(value)
      ? `${value}Z`
      : value;
  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

const format = (
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions,
  timeZone: string
): string => {
  const date = parseApiDate(value);
  if (!date) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    ...options,
    timeZone: resolveTimeZone(timeZone),
  }).format(date);
};

export const formatDateTime = (
  value: string | number | Date,
  timeZone = APP_TIME_ZONE
): string =>
  format(
    value,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    },
    timeZone
  );

export const formatDate = (
  value: string | number | Date,
  timeZone = APP_TIME_ZONE
): string =>
  format(
    value,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    timeZone
  );

export const formatTime = (
  value: string | number | Date,
  timeZone = APP_TIME_ZONE
): string =>
  format(
    value,
    {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    },
    timeZone
  );
